import { apiGet } from "../services/restApiService";

/**
 * Org-admin Play signals + evidence export (PR4 backend surface,
 * `/admin/play/*`). Types are verbatim from `play-signals.service.ts` /
 * `play-export.service.ts`. Both endpoints are VIEWER-ORG-SCOPED — they
 * take no organisation parameter; EntitlementsGuard resolves the caller's
 * own org (which is why the admin console's Play page is home-org-only).
 *
 * `SuppressedCount` is the n<5 privacy device: a number OR the literal
 * string "<5". NEVER do arithmetic on these values — render them verbatim.
 */
export type SuppressedCount = number | "<5";

export type PlaySignalsRange = "7d" | "30d" | "90d";

export interface PlaySignals {
  range: { window: string; from: string; to: string };
  completionsByFamily: Array<{ family: string; completions: SuppressedCount }>;
  safetyEscalationCount: SuppressedCount;
  commonRiskFlags: Array<{ category: string; occurrences: SuppressedCount }>;
  /** North-Star Human Oversight Ratio — null until enough drafts exist. */
  humanOversightRatio: number | null;
  reviewQualityDistribution: Array<{ rating: number; count: SuppressedCount }>;
  evidenceCandidateCount: SuppressedCount;
  readinessMovement: {
    currentPeriodEvidence: SuppressedCount;
    previousPeriodEvidence: SuppressedCount;
  };
  usageCostByFamily: Array<{
    feature: string;
    calls: SuppressedCount;
    promptTokens: number | null;
    completionTokens: number | null;
    costCents: number | null;
  }>;
  allowance: {
    used: number;
    limit: number;
    resetsAt: string;
    source: string;
  };
}

/** The two admin-readable rungs — the only exportable visibilities. */
export type ExportableVisibility =
  | "admin_evidence_candidate"
  | "governor_pack_approved_input";

export interface PlayEvidenceExportRecordV1 {
  contract_version: "play-evidence-export-v1";
  [key: string]: unknown;
}

export interface PlayEvidenceExportResponse {
  contract_version: "play-evidence-export-v1";
  items: PlayEvidenceExportRecordV1[];
  total: number;
  limit: number;
  offset: number;
}

export const getPlaySignals = (range: PlaySignalsRange): Promise<PlaySignals> =>
  apiGet<PlaySignals>(`/admin/play/signals?range=${range}`);

export const getPlayEvidenceExport = (params: {
  visibility?: ExportableVisibility;
  limit?: number;
  offset?: number;
}): Promise<PlayEvidenceExportResponse> => {
  const search = new URLSearchParams();
  if (params.visibility) search.set("visibility", params.visibility);
  search.set("limit", String(params.limit ?? 50));
  search.set("offset", String(params.offset ?? 0));
  return apiGet<PlayEvidenceExportResponse>(
    `/admin/play/evidence-export?${search.toString()}`
  );
};
